import { StyleProvider } from '@ant-design/cssinjs';
import { App as AntApp, ConfigProvider } from 'antd';

import type { JSX, PropsWithChildren } from 'react';
import { useEffect } from 'react';

import { antdHolder } from '@/components/ui/antd-holder';
import { getAntdThemeConfig } from '@/styles/theme';

/**
 * AntdProvider component that configures Ant Design's theme and provides
 * access to Ant Design's imperative APIs (message, notification, modal).
 *
 * This provider:
 * - Applies custom theme configuration from theme.ts
 * - Reads CSS variables for dynamic theming
 * - Bridges Ant Design's APIs to a global holder for use outside React components
 * - Wraps the app with StyleProvider for CSS-in-JS support
 */
export default function AntdProvider({ children }: PropsWithChildren): JSX.Element {
  return (
    <StyleProvider layer>
      <ConfigProvider theme={getAntdThemeConfig()}>
        <AntApp>
          <AntdAppBridge />
          {children}
        </AntApp>
      </ConfigProvider>
    </StyleProvider>
  );
}

/**
 * Internal component that bridges Ant Design's App APIs to the global holder.
 * This allows using message, notification, and modal APIs outside React components.
 */
function AntdAppBridge() {
  const api = AntApp.useApp();

  useEffect(() => {
    antdHolder.message = api.message;
    antdHolder.notification = api.notification;
    antdHolder.modal = api.modal;

    return () => {
      antdHolder.message = null;
      antdHolder.notification = null;
      antdHolder.modal = null;
    };
  }, [api]);

  return null;
}
